/**
 * TestSeriesImportPage — bulk-create a whole Test Series from one Excel workbook.
 * Reuses the exact assessment import template (Assessments + Questions sheets);
 * every imported assessment becomes a member test of a new draft series.
 */
import React, { useState, useRef } from "react";
import { Button, Spinner, Alert, Table, Card, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Upload, Download, CheckCircle, AlertCircle, FileText } from "lucide-react";
import * as XLSX from "xlsx";
import apiClient from "../../utils/apiClient";
import { PageHeader, AppButton } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { seriesImportTrail } from "../QuizBank/quizCenterTrail";
import "../Assessments/Assessments-v2.scss";

const TestSeriesImportPage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [seriesTitle, setSeriesTitle] = useState("");
  const [category, setCategory] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [step, setStep] = useState("upload"); // upload | preview | done

  const downloadSample = () => {
    const wb = XLSX.utils.book_new();
    const assessmentData = [
      { "Assessment Name": "Mock Test 1", "Description": "Full-length mock", "Mode": "exam", "Time Limit (min)": 60, "Max Attempts": 1, "Passing %": 50, "Shuffle Questions": "TRUE", "Negative Marking": "TRUE", "Assign To Type": "open", "Batch Names": "" },
      { "Assessment Name": "Mock Test 2", "Description": "Full-length mock", "Mode": "exam", "Time Limit (min)": 60, "Max Attempts": 1, "Passing %": 50, "Shuffle Questions": "TRUE", "Negative Marking": "TRUE", "Assign To Type": "open", "Batch Names": "" },
    ];
    const questionData = [
      { "Assessment Name": "Mock Test 1", "Question Text": "Speed of light (approx, m/s)?", "Type": "single_choice", "Difficulty": "easy", "Marks": 4, "Negative Marks": 1, "Option A": "3×10^8", "Option B": "3×10^6", "Option C": "3×10^5", "Option D": "3×10^9", "Correct Option": "A", "Explanation": "c ≈ 3×10^8 m/s", "Tags": "physics" },
      { "Assessment Name": "Mock Test 2", "Question Text": "Powerhouse of the cell?", "Type": "single_choice", "Difficulty": "easy", "Marks": 4, "Negative Marks": 1, "Option A": "Nucleus", "Option B": "Mitochondria", "Option C": "Ribosome", "Option D": "Golgi", "Correct Option": "B", "Explanation": "Mitochondria.", "Tags": "biology" },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(assessmentData), "Assessments");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(questionData), "Questions");
    XLSX.writeFile(wb, "test-series-import-template.xlsx");
  };

  const parseExcelFile = (f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const aSheet = wb.Sheets["Assessments"];
        const qSheet = wb.Sheets["Questions"];
        if (!aSheet || !qSheet) {
          setErrors(["Workbook must contain 'Assessments' and 'Questions' sheets"]);
          setToast({ show: true, message: "Invalid template", type: "error" });
          return;
        }
        const assessments = XLSX.utils.sheet_to_json(aSheet);
        const questions = XLSX.utils.sheet_to_json(qSheet);

        const parseErrors = [];
        const valid = [];
        assessments.forEach((a, idx) => {
          if (!a["Assessment Name"]?.trim()) { parseErrors.push(`Row ${idx + 2}: missing assessment name`); return; }
          const qCount = questions.filter((q) => q["Assessment Name"] === a["Assessment Name"]).length;
          if (qCount === 0) { parseErrors.push(`"${a["Assessment Name"]}": no questions`); return; }
          valid.push({ ...a, questionCount: qCount });
        });

        setParsedData({ assessments: valid, questions, totalQuestionsCount: questions.length });
        setErrors(parseErrors);
        if (parseErrors.length === 0) {
          setStep("preview");
          setToast({ show: true, message: "Parsed successfully", type: "success" });
        } else {
          setToast({ show: true, message: `${parseErrors.length} errors found`, type: "error" });
        }
      } catch (err) {
        setErrors([`Failed to parse: ${err.message}`]);
        setToast({ show: true, message: "Failed to parse Excel file", type: "error" });
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (!selected.name.match(/\.(xlsx|xls)$/i)) {
      setToast({ show: true, message: "Please upload an Excel file", type: "error" });
      return;
    }
    setFile(selected);
    setStep("upload");
    setParsedData(null);
    setErrors([]);
    if (!seriesTitle) setSeriesTitle(selected.name.replace(/\.(xlsx|xls)$/i, ""));
    parseExcelFile(selected);
  };

  const handleImport = async () => {
    if (!parsedData?.assessments.length) return;
    if (!seriesTitle.trim()) {
      setToast({ show: true, message: "Enter a series title", type: "error" });
      return;
    }
    setUploading(true);
    try {
      const { data } = await apiClient.post("/test-series/import-bundle", {
        title: seriesTitle.trim(),
        category,
        assessments: parsedData.assessments,
        questions: parsedData.questions,
      }, { headers: { "X-Branch-Id": "all" } });
      setResult(data);
      setStep("done");
      setToast({ show: true, message: `Imported ${data.testsCreated} tests`, type: "success" });
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Import failed", type: "error" });
    }
    setUploading(false);
  };

  return (
    <div className="assessment-import-page">
      <PageHeader icon={Upload} title="Import Test Series"
        description="Bulk-create a series and all its tests from one Excel workbook"
        breadcrumbs={seriesImportTrail()} />

      {step === "upload" && (
        <Card className="import-card">
          <Card.Body className="text-center py-5">
            <Upload size={48} className="text-muted mb-3" />
            <h5 className="mb-3">Upload Excel File</h5>
            <p className="text-muted mb-4">Uses the same template as assessment import — each assessment becomes one test in the series.</p>
            <div className="upload-zone" onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.name.match(/\.(xlsx|xls)$/i)) { setFile(f); parseExcelFile(f); } }}>
              <FileText size={32} className="text-muted mb-2" />
              <p>Drop file here or click to browse</p>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: "none" }} />
            </div>
            {file && <div className="mt-3 text-success">✓ {file.name}</div>}
            <div className="mt-4">
              <Button variant="outline-secondary" size="sm" onClick={downloadSample}>
                <Download size={14} className="me-2" /> Download Sample
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {step === "preview" && parsedData && (
        <div>
          {errors.length > 0 && (
            <Alert variant="warning" className="mb-3">
              <AlertCircle size={16} className="me-2" /><strong>{errors.length} error(s)</strong>
              <ul className="mt-2 mb-0">{errors.slice(0, 5).map((er, i) => <li key={i} className="small">{er}</li>)}</ul>
            </Alert>
          )}

          <Card className="mb-3">
            <Card.Body>
              <div className="row g-3">
                <div className="col-md-7">
                  <label className="form-label small fw-bold">Series Title *</label>
                  <input className="form-control" value={seriesTitle} onChange={(e) => setSeriesTitle(e.target.value)} placeholder="e.g. NEET 2026 Grand Test Series" />
                </div>
                <div className="col-md-5">
                  <label className="form-label small fw-bold">Category</label>
                  <input className="form-control" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. NEET" />
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header className="bg-light">
              <h6 className="mb-0"><CheckCircle size={16} className="me-2" style={{ color: "green" }} /> Tests ({parsedData.assessments.length})</h6>
            </Card.Header>
            <Table hover size="sm" className="mb-0">
              <thead><tr><th>Name</th><th>Mode</th><th>Time</th><th>Questions</th></tr></thead>
              <tbody>
                {parsedData.assessments.map((a, i) => (
                  <tr key={i}>
                    <td className="fw-bold">{a["Assessment Name"]}</td>
                    <td><Badge bg={a["Mode"] === "practice" ? "info" : "warning"}>{a["Mode"]}</Badge></td>
                    <td>{a["Time Limit (min)"]}m</td>
                    <td><Badge bg="light" text="dark">{a.questionCount}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <div className="mt-4 d-flex gap-2 justify-content-end">
            <Button variant="light" onClick={() => { setStep("upload"); setFile(null); setParsedData(null); }}>← Change File</Button>
            <AppButton type="button" onClick={handleImport} disabled={uploading || errors.length > 0 || !seriesTitle.trim()}>
              {uploading ? <Spinner size="sm" className="me-2" /> : null} Create Series
            </AppButton>
          </div>
        </div>
      )}

      {step === "done" && result && (
        <Card className="import-success">
          <Card.Body className="text-center py-5">
            <CheckCircle size={64} style={{ color: "#20c997" }} className="mb-3" />
            <h5 className="mb-3">Series created! ✓</h5>
            <p className="text-success mb-3">Imported {result.testsCreated} tests ({result.questionsCreated} questions) into a draft series.</p>
            <div className="mt-4 d-flex gap-2 justify-content-center">
              <Button variant="outline-primary" onClick={() => navigate(`/test-series/${result.series._id}`)}>Open Series</Button>
              <AppButton type="button" onClick={() => { setStep("upload"); setFile(null); setParsedData(null); setResult(null); setSeriesTitle(""); }}>Import Another</AppButton>
            </div>
          </Card.Body>
        </Card>
      )}

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default TestSeriesImportPage;
